/**
 * Action Planning — Compose validated action plans from classified intents.
 *
 * Builds single-action and multi-step workflow plans with parameter population,
 * inputsFrom references, and implicit dependency resolution.
 *
 * @module gibbs/text-to-action/planActions
 */

import { getAction, checkPreconditions } from '../registry/index';

/**
 * @typedef {Object} PlannedAction
 * @property {string} actionId
 * @property {Object} params - Populated parameters
 * @property {Object} resolvedInputs - Resolved input references (e.g., {caseData: "current"})
 * @property {number} [step] - Step number for workflows
 * @property {Object} [inputsFrom] - References to prior step outputs
 */

/**
 * @typedef {Object} ActionPlan
 * @property {'single_action' | 'workflow'} intent
 * @property {PlannedAction[]} actions
 * @property {string} [summary] - Human-readable workflow summary
 */

/**
 * Build an action plan from a classified intent.
 *
 * For single_action: creates a one-action plan with populated params and resolvedInputs.
 * For workflow: creates a multi-step plan with step ordering and inputsFrom references.
 * Both modes resolve implicit dependencies (e.g., prepending a load step if a
 * precondition is unmet).
 *
 * @param {import('./classifyIntent.js').ClassifiedIntent} classified - Classified intent from LLM
 * @param {Object} workspaceState - Current workspace state for precondition checks
 * @returns {ActionPlan}
 */
export function planActions(classified, workspaceState) {
    if (classified.intent === 'single_action') {
        return planSingleAction(classified, workspaceState);
    }
    if (classified.intent === 'workflow') {
        return planWorkflow(classified, workspaceState);
    }
    // For clarify/chat, pass through
    return {
        intent: classified.intent,
        actions: [],
        ...(classified.question && { question: classified.question }),
        ...(classified.options && { options: classified.options }),
        ...(classified.missing && { missing: classified.missing }),
        ...(classified.context && { context: classified.context }),
        ...(classified.message && { message: classified.message }),
    };
}

/**
 * Resolve default values for an action's parameters. Merges user-supplied params
 * with defaults from the ActionDef.
 *
 * @param {import('../registry/schema.js').ActionDef} actionDef
 * @param {Object} userParams - Parameters from the LLM response
 * @returns {Object} Merged parameters with defaults filled in
 */
export function resolveDefaults(actionDef, userParams = {}) {
    const merged = { ...userParams };
    for (const paramDef of actionDef.parameters) {
        if (!(paramDef.name in merged) && paramDef.defaultValue !== undefined) {
            merged[paramDef.name] = paramDef.defaultValue;
        }
    }
    return merged;
}

/**
 * Resolve input references from workspace state.
 * Maps implicit references like "active" and "current" based on context.
 *
 * @param {import('../registry/schema.js').ActionDef} actionDef
 * @param {Object} userParams - Parameters that may contain input values
 * @returns {Object} resolvedInputs map (inputName -> resolved reference)
 */
export function resolveInputs(actionDef, userParams = {}) {
    const resolved = {};
    for (const input of actionDef.inputs) {
        if (input.name in userParams) {
            resolved[input.name] = userParams[input.name];
        } else {
            // Apply sensible defaults for common input types
            resolved[input.name] = defaultInputReference(input);
        }
    }
    return resolved;
}

// ─── Single Action Planning ─────────────────────────────────────────────────

/**
 * @param {import('./classifyIntent.js').ClassifiedIntent} classified
 * @param {Object} workspaceState
 * @returns {ActionPlan}
 */
function planSingleAction(classified, workspaceState) {
    const actionSpec = classified.actions[0];
    if (!actionSpec) {
        return { intent: 'single_action', actions: [] };
    }

    const actionDef = getAction(actionSpec.actionId);
    if (!actionDef) {
        return {
            intent: 'single_action',
            actions: [{
                actionId: actionSpec.actionId,
                params: actionSpec.params || {},
                resolvedInputs: {},
            }],
        };
    }

    const params = resolveDefaults(actionDef, actionSpec.params || {});
    const resolvedInputs = resolveInputs(actionDef, {
        ...params,
        ...(actionSpec.resolvedInputs || {}),
    });

    // Check preconditions and prepend dependency steps if needed
    const prereqSteps = resolveImplicitDependencies(actionSpec.actionId, workspaceState);

    if (prereqSteps.length === 0) {
        return {
            intent: 'single_action',
            actions: [{
                actionId: actionSpec.actionId,
                params,
                resolvedInputs,
            }],
        };
    }

    // If we need prerequisite steps, promote to a workflow
    const allSteps = [];
    for (let i = 0; i < prereqSteps.length; i++) {
        allSteps.push({
            actionId: prereqSteps[i].actionId,
            params: prereqSteps[i].params,
            resolvedInputs: prereqSteps[i].resolvedInputs || {},
            step: i + 1,
        });
    }
    allSteps.push({
        actionId: actionSpec.actionId,
        params,
        resolvedInputs,
        step: prereqSteps.length + 1,
    });

    return {
        intent: 'workflow',
        actions: allSteps,
        summary: `Prerequisite steps added to satisfy preconditions for ${actionDef.name}.`,
    };
}

// ─── Workflow Planning ──────────────────────────────────────────────────────

/**
 * @param {import('./classifyIntent.js').ClassifiedIntent} classified
 * @param {Object} workspaceState
 * @returns {ActionPlan}
 */
function planWorkflow(classified, workspaceState) {
    const actions = classified.actions || [];

    // Collect all prerequisite steps for the first action in the workflow
    const firstAction = actions[0];
    const prereqSteps = firstAction
        ? resolveImplicitDependencies(firstAction.actionId, workspaceState)
        : [];

    const allSteps = [];
    let stepNum = 1;

    // Add prerequisite steps
    for (const prereq of prereqSteps) {
        allSteps.push({
            actionId: prereq.actionId,
            params: prereq.params,
            resolvedInputs: prereq.resolvedInputs || {},
            step: stepNum++,
        });
    }

    // Add workflow steps with proper numbering and inputsFrom resolution
    for (let i = 0; i < actions.length; i++) {
        const actionSpec = actions[i];
        const actionDef = getAction(actionSpec.actionId);

        const params = actionDef
            ? resolveDefaults(actionDef, actionSpec.params || {})
            : (actionSpec.params || {});

        const resolvedInputs = actionDef
            ? resolveInputs(actionDef, { ...params, ...(actionSpec.resolvedInputs || {}) })
            : (actionSpec.resolvedInputs || {});

        const step = {
            actionId: actionSpec.actionId,
            params,
            resolvedInputs,
            step: stepNum++,
        };

        // Preserve or build inputsFrom references
        if (actionSpec.inputsFrom) {
            // Sanitize: the LLM sometimes puts workspace literals
            // ("active", "current", "all") in inputsFrom. Those are
            // workspace references, not step references — move them
            // to resolvedInputs instead.
            const WORKSPACE_LITERALS = new Set(['active', 'current', 'all']);
            const sanitizedInputsFrom = {};
            for (const [key, value] of Object.entries(actionSpec.inputsFrom)) {
                if (typeof value === 'string' && WORKSPACE_LITERALS.has(value)) {
                    step.resolvedInputs[key] = value;
                } else {
                    sanitizedInputsFrom[key] = value;
                }
            }
            if (Object.keys(sanitizedInputsFrom).length > 0) {
                step.inputsFrom = sanitizedInputsFrom;
            }
        } else if (i > 0) {
            // Attempt to auto-link inputs from previous step's outputs
            const prevActionSpec = actions[i - 1];
            const prevDef = getAction(prevActionSpec.actionId);
            if (prevDef && actionDef) {
                const autoLinks = autoLinkInputs(prevDef, actionDef, step.step - 1);
                if (Object.keys(autoLinks).length > 0) {
                    step.inputsFrom = autoLinks;
                }
            }
        }

        allSteps.push(step);
    }

    return {
        intent: 'workflow',
        actions: allSteps,
        summary: classified.summary || generatePlanSummary(allSteps),
    };
}

// ─── Dependency Resolution ──────────────────────────────────────────────────

/**
 * Map of precondition check IDs to actionIds that can satisfy them.
 * @type {Object<string, {actionId: string, params: Object}>}
 */
const PRECONDITION_RESOLVERS = {
    protein_loaded: {
        actionId: 'load-protein-from-pdb',
        params: {},
        resolvedInputs: {},
    },
    compound_loaded: {
        actionId: 'load-starter-compounds',
        params: {},
        resolvedInputs: {},
    },
    fragment_map_available: {
        actionId: 'load-fragment-map',
        params: {},
        resolvedInputs: {},
    },
};

/**
 * Check which preconditions are unmet for an action and return actions to satisfy them.
 *
 * @param {string} actionId
 * @param {Object} workspaceState
 * @returns {Array<{actionId: string, params: Object, resolvedInputs?: Object}>}
 */
function resolveImplicitDependencies(actionId, workspaceState) {
    if (!workspaceState) return [];

    const { met, unmet } = checkPreconditions(actionId, workspaceState);
    if (met) return [];

    const steps = [];
    for (const preconditionId of unmet) {
        const resolver = PRECONDITION_RESOLVERS[preconditionId];
        if (resolver) {
            steps.push({ ...resolver });
        }
    }
    return steps;
}

/**
 * Auto-link inputs from a previous step's outputs based on type compatibility.
 *
 * @param {import('../registry/schema.js').ActionDef} prevDef - Previous action definition
 * @param {import('../registry/schema.js').ActionDef} currDef - Current action definition
 * @param {number} prevStep - Previous step number
 * @returns {Object} inputsFrom map
 */
function autoLinkInputs(prevDef, currDef, prevStep) {
    const links = {};
    for (const input of currDef.inputs) {
        for (const output of prevDef.outputs) {
            // Direct type match or compatible types
            if (output.type === input.type) {
                links[input.name] = `step:${prevStep}.${output.name}`;
                break;
            }
        }
    }
    return links;
}

/**
 * Default input reference for common types when no explicit value is given.
 * @param {import('../registry/schema.js').ParamDef} input
 * @returns {string}
 */
function defaultInputReference(input) {
    const typeDefaults = {
        CompoundList: 'active',
        Compound: 'active',
        CaseData: 'current',
        ProteinStructure: 'current',
        FragmentMap: 'current',
    };
    return typeDefaults[input.type] || 'current';
}

/**
 * Generate a simple summary from planned actions.
 * @param {PlannedAction[]} steps
 * @returns {string}
 */
function generatePlanSummary(steps) {
    if (steps.length === 0) return '';
    const names = steps.map((s) => {
        const def = getAction(s.actionId);
        return def ? def.name : s.actionId;
    });
    if (names.length === 1) return names[0];
    const last = names.pop();
    return `${names.join(', ')}, then ${last}`;
}
